import { pauseAllMedia, removeTrapFocus, trapFocus } from '../lib/ui-helpers.ts';

type ProductModelElement = HTMLElement & {
  modelViewerUI?: {
    pause: () => void;
  };
};

export const defineModalDialogCustomElement = (): void => {
  if (customElements.get('modal-dialog')) return;

  class ModalDialog extends HTMLElement {
    moved?: boolean;
    openedBy?: HTMLElement | null;

    constructor() {
      super();
      let modalClose = this.querySelector<HTMLElement>('[id^="ModalClose-"]');
      if (modalClose) {
        modalClose.addEventListener('click', this.hide.bind(this));
      }

      this.addEventListener('keyup', (event: KeyboardEvent) => {
        if (event.code.toUpperCase() === 'ESCAPE') this.hide();
      });

      if (this.classList.contains('media-modal')) {
        this.addEventListener('pointerup', (event: PointerEvent) => {
          const target = event.target as HTMLElement | null;
          if (
            event.pointerType === 'mouse' &&
            !target?.closest('deferred-media, product-model')
          )
            this.hide();
        });
      } else {
        this.addEventListener('click', (event: MouseEvent) => {
          const target = event.target as HTMLElement | null;
          if (target?.nodeName === 'MODAL-DIALOG') this.hide();
        });
      }
    }

    connectedCallback() {
      if (this.moved) return;
      this.moved = true;
      document.body.appendChild(this);
    }

    show(opener: HTMLElement | null) {
      this.openedBy = opener;
      const popup = this.querySelector<HTMLElement & { loadContent?: () => void }>(
        '.template-popup',
      );
      document.body.classList.add('--no-scroll');
      this.setAttribute('open', '');
      popup?.loadContent?.();
      trapFocus(this, this.querySelector<HTMLElement>('[role="dialog"]'));
      pauseAllMedia();
    }

    hide() {
      document.body.classList.remove('--no-scroll');
      this.removeAttribute('open');
      removeTrapFocus(this.openedBy ?? null);
      pauseAllMedia();
    }

    pauseAllMedia() {
      document.querySelectorAll<HTMLIFrameElement>('.js-youtube').forEach((video) => {
        video.contentWindow?.postMessage(
          '{"event":"command","func":"' + 'pauseVideo' + '","args":""}',
          '*',
        );
      });
      document.querySelectorAll<HTMLIFrameElement>('.js-vimeo').forEach((video) => {
        video.contentWindow?.postMessage('{"method":"pause"}', '*');
      });
      document.querySelectorAll<HTMLVideoElement>('video').forEach((video) => video.pause());
      document
        .querySelectorAll<ProductModelElement>('product-model')
        .forEach((model) => model.modelViewerUI?.pause());
    }
  }

  customElements.define('modal-dialog', ModalDialog);

  (window as any).ModalDialog = (window as any).ModalDialog || ModalDialog;
  (globalThis as any).ModalDialog = (globalThis as any).ModalDialog || ModalDialog;

  if (!customElements.get('quick-add-modal')) {
    customElements.define(
      'quick-add-modal',
      class QuickAddModal extends ModalDialog {
        modalContent: HTMLElement | null;

        constructor() {
          super();
          this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

          this.addEventListener('product-info:loaded', (event: Event) => {
            const target = (event as any)?.target;
            target?.addPreProcessCallback?.(this.preprocessHTML.bind(this));
          });
        }

        hide(preventFocus = false) {
          const cartNotification =
            document.querySelector<any>('cart-notification') ||
            document.querySelector<any>('cart-drawer');
          if (cartNotification) cartNotification.setActiveElement?.(this.openedBy);
          if (this.modalContent) this.modalContent.innerHTML = '';

          if (preventFocus) this.openedBy = null;
          super.hide();
        }

        show(opener: HTMLElement) {
          opener.setAttribute('aria-disabled', 'true');
          opener.classList.add('loading');
          opener.querySelector('.loading__spinner')?.classList.remove('hidden');

          fetch(opener.getAttribute('data-product-url') || '')
            .then((response) => response.text())
            .then((responseText) => {
              const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
              const productElement = responseHTML.querySelector('product-info');

              this.preprocessHTML(productElement as any);
              const HTMLUpdateUtility = window.HTMLUpdateUtility;
              if (this.modalContent && HTMLUpdateUtility?.setInnerHTML) {
                HTMLUpdateUtility.setInnerHTML(this.modalContent, (productElement as any).outerHTML);
              } else if (this.modalContent) {
                this.modalContent.innerHTML = (productElement as any)?.outerHTML || '';
              }

              if (window.Shopify?.PaymentButton?.init) {
                window.Shopify.PaymentButton.init();
              }
              if (window.ProductModel?.loadShopifyXR) window.ProductModel.loadShopifyXR();

              super.show(opener);
            })
            .finally(() => {
              opener.removeAttribute('aria-disabled');
              opener.classList.remove('loading');
              opener.querySelector('.loading__spinner')?.classList.add('hidden');
            });
        }

        preprocessHTML(productElement: HTMLElement) {
          if (!productElement || !this.modalContent) return;

          productElement.classList.forEach((classApplied) => {
            if (classApplied.startsWith('color-') || classApplied === 'gradient')
              this.modalContent?.classList.add(classApplied);
          });
          this.preventDuplicatedIDs(productElement);
          this.removeDOMElements(productElement);
          this.removeGalleryListSemantic(productElement);
          this.updateImageSizes(productElement);
          this.preventVariantURLSwitching(productElement);
        }

        preventVariantURLSwitching(productElement: HTMLElement) {
          productElement.setAttribute('data-update-url', 'false');
        }

        removeDOMElements(productElement: HTMLElement) {
          const pickupAvailability = productElement.querySelector('pickup-availability');
          if (pickupAvailability) pickupAvailability.remove();

          const summarys = productElement.querySelectorAll('.product__accordion.accordion');
          if (summarys) summarys.forEach((summary) => summary.remove());

          const mediaGallery = productElement.querySelector('.thumbnail-slider');
          if (mediaGallery) mediaGallery.classList.add('--hidden');

          const productModal = productElement.querySelector('product-modal');
          if (productModal) productModal.remove();

          const modalDialog = productElement.querySelectorAll('modal-dialog');
          if (modalDialog) modalDialog.forEach((modal) => modal.remove());
        }

        preventDuplicatedIDs(productElement: HTMLElement) {
          const sectionId = this.dataset.section;
          if (!sectionId) return;
          window.HTMLUpdateUtility?.prependSectionId?.(productElement, sectionId);
        }

        removeGalleryListSemantic(productElement: HTMLElement) {
          const gallery = productElement.querySelector<any>('media-gallery');
          gallery?.removeListSemantic?.();
        }

        updateImageSizes(productElement: HTMLElement) {
          const product = productElement.querySelector('product-info');
          if (!product) return;
          const images = product.querySelectorAll<HTMLImageElement>('img');
          images.forEach((image) => {
            image.removeAttribute('loading');
          });
        }
      },
    );
  }
};
